<template>
	<div class="rfc">
		<div class="rfc-row">
			<button
				type="button"
				class="rfc-chip"
				:class="{ 'rfc-chip--on': isPrecomputed }"
				:disabled="loading || saving"
				@click="toggleMode"
			>
				{{ isPrecomputed ? 'Activado' : 'Desactivado' }}
			</button>
			<div class="rfc-text">
				<span class="rfc-title">Precalcular reporte financiero</span>
				<span class="rfc-hint">{{ hint }}</span>
			</div>
		</div>

		<div v-if="state" class="rfc-status">
			<span class="rfc-dot" :class="'rfc-dot--' + tone" aria-hidden="true"></span>
			<span class="rfc-status-label">{{ statusLabel }}</span>
			<span v-if="isConsolidated && state.members_total" class="rfc-meta">
				{{ state.members_fresh }} de {{ state.members_total }} miembros al día
			</span>
			<span v-if="state.built_at" class="rfc-meta">
				Última construcción {{ formatDate(state.built_at) }}<template v-if="state.rows !== null"> · {{ state.rows }} filas</template>
			</span>
		</div>
		<div v-if="isRunning" class="rfc-track" role="progressbar" aria-label="Recalculando"><div class="rfc-track-fill"></div></div>
		<p v-if="state && state.error" class="rfc-error">{{ state.error }}</p>

		<div class="rfc-actions">
			<SimpleButton
				class="act"
				:disabled="loading || saving || isRunning"
				@click="rebuild"
			>
				<span class="act__text">Recalcular ahora</span>
				<span class="act__icon" aria-hidden="true">⟳</span>
			</SimpleButton>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import SimpleButton from "@/components/modals/SimpleButton";
import {
	getReportFactStatus,
	rebuildReportFact,
	setReportMode,
} from "@/api/carteraAnalista";
import { extractErrorMessage, notificate } from "@/utils/message";

// Mismo contrato de sondeo que el import del mayor (CompanyInfo_UploadLedger).
export const POLL_INTERVAL_MS = 3000;
export const POLL_MAX_ATTEMPTS = 100;

const STATUS_LABELS = {
	"": { label: "Sin calcular", tone: "off" },
	P: { label: "Recalculando…", tone: "progress" },
	// Debounce (etapa 2): el recálculo espera 90 s desde el último cambio.
	P_SCHEDULED: { label: "Recálculo programado: espera los últimos cambios…", tone: "progress" },
	S_FRESH: { label: "Al día", tone: "ok" },
	S_STALE: { label: "Desactualizado: hay cambios posteriores al último cálculo", tone: "warn" },
	E: { label: "Error en el último cálculo", tone: "bad" },
};

export default {
	name: "ReportFactControl",
	components: { SimpleButton },
	props: {
		companyId: { type: [Number, String], required: true },
	},
	data() {
		return {
			state: null,
			loading: true,
			saving: false,
			pollTimer: null,
			pollAttempts: 0,
		};
	},
	computed: {
		isPrecomputed() {
			return !!this.state && this.state.report_mode === "P";
		},
		isConsolidated() {
			return !!this.state && !!this.state.is_consolidated;
		},
		isRunning() {
			return !!this.state && (this.state.status === "P" || !!this.state.job_running);
		},
		statusKey() {
			if (!this.state) return "";
			if (this.state.status === "S") return this.state.is_fresh ? "S_FRESH" : "S_STALE";
			if (this.state.status === "P" && this.state.job_scheduled && !this.state.job_running) {
				return "P_SCHEDULED";
			}
			return this.state.status in STATUS_LABELS ? this.state.status : "";
		},
		statusLabel() {
			return STATUS_LABELS[this.statusKey].label;
		},
		tone() {
			return STATUS_LABELS[this.statusKey].tone;
		},
		hint() {
			if (this.isConsolidated) {
				const n = this.state.members_total || 0;
				const members = n === 1 ? "su empresa miembro" : `sus ${n} empresas miembro`;
				return this.isPrecomputed
					? `El reporte consolidado lee las tablas precalculadas de ${members}; se recalculan solas tras cada cambio.`
					: `Activa el precálculo aquí y también en ${members}: el reporte consolidado lee sus tablas.`;
			}
			return this.isPrecomputed
				? "Los reportes leen una tabla precalculada que se recalcula sola tras cada cambio; mientras tanto muestran la versión anterior con un aviso."
				: "Cada consulta del reporte se calcula en el momento.";
		},
	},
	mounted() {
		this.loadStatus();
	},
	beforeDestroy() {
		this.clearPollTimer();
	},
	methods: {
		formatDate(value) {
			return moment(value).format("DD-MM-YYYY HH:mm");
		},
		async loadStatus() {
			this.loading = true;
			try {
				const { data } = await getReportFactStatus(this.companyId);
				this.applyState(data);
			} catch (error) {
				notificate(extractErrorMessage(error, "No se pudo leer el estado del precálculo."), "is-danger");
			} finally {
				this.loading = false;
			}
		},
		applyState(data) {
			this.state = data;
			if (this.isRunning) {
				this.schedulePoll();
			} else {
				this.clearPollTimer();
			}
		},
		async toggleMode() {
			const mode = this.isPrecomputed ? "L" : "P";
			this.saving = true;
			try {
				const { data } = await setReportMode(this.companyId, mode);
				this.applyState(data);
				notificate(
					mode === "P"
						? "Precálculo activado. El primer cálculo puede tardar unos minutos."
						: "Precálculo desactivado: el reporte vuelve a calcularse en vivo.",
					"is-success",
				);
			} catch (error) {
				notificate(extractErrorMessage(error, "No se pudo cambiar el modo del reporte."), "is-danger");
			} finally {
				this.saving = false;
			}
		},
		async rebuild() {
			this.saving = true;
			try {
				const { data } = await rebuildReportFact(this.companyId);
				this.applyState(data);
				notificate("Recálculo encolado.", "is-success");
			} catch (error) {
				notificate(extractErrorMessage(error, "No se pudo encolar el recálculo."), "is-danger");
			} finally {
				this.saving = false;
			}
		},
		schedulePoll() {
			this.clearPollTimer();
			this.pollTimer = setTimeout(this.poll, POLL_INTERVAL_MS);
		},
		async poll() {
			this.pollTimer = null;
			this.pollAttempts += 1;
			if (this.pollAttempts > POLL_MAX_ATTEMPTS) {
				this.pollAttempts = 0;
				notificate("El recálculo sigue en curso; revisa más tarde.", "is-warning");
				return;
			}
			try {
				const { data } = await getReportFactStatus(this.companyId);
				const wasRunning = this.isRunning;
				this.applyState(data);
				if (wasRunning && !this.isRunning) {
					this.pollAttempts = 0;
					notificate(
						data.status === "E"
							? "El recálculo terminó con error."
							: "Reporte precalculado al día.",
						data.status === "E" ? "is-danger" : "is-success",
					);
				}
			} catch (error) {
				this.schedulePoll();
			}
		},
		clearPollTimer() {
			if (this.pollTimer) {
				clearTimeout(this.pollTimer);
				this.pollTimer = null;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
// Vive dentro de una fila de la ficha: se apila en vertical y todo envuelve;
// nunca fuerza un ancho mínimo (antes, en una columna angosta, el chip y el
// título competían en una sola línea y desbordaban).
.rfc {
	display: flex;
	flex-direction: column;
	gap: 10px;
	max-width: 640px;
	min-width: 0;
}
.rfc-row {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 8px 14px;
}
.rfc-text {
	flex: 1 1 220px;
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	padding-top: 4px;
}
.rfc-title {
	font-size: 0.9rem;
	font-weight: 600;
	color: var(--ap-ink);
}
.rfc-hint,
.rfc-meta {
	font-size: 0.8rem;
	line-height: 1.45;
	color: var(--ap-muted);
}
.rfc-chip {
	flex-shrink: 0;
	min-width: 112px;
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-pill);
	background: var(--ap-paper);
	color: var(--ap-ink-2);
	padding: 6px 14px;
	font: inherit;
	font-size: 0.85rem;
	font-weight: 500;
	cursor: pointer;
	transition: background-color 0.16s var(--ap-ease), color 0.16s var(--ap-ease),
		border-color 0.16s var(--ap-ease);
	&--on {
		background: var(--ap-teal);
		border-color: var(--ap-teal-deep);
		color: var(--ap-paper);
	}
	&:disabled {
		cursor: default;
		opacity: 0.6;
	}
	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}
.rfc-status {
	display: flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 2px 8px;
	font-size: 0.85rem;
	color: var(--ap-ink-2);
}
.rfc-dot {
	align-self: center;
	width: 0.55rem;
	height: 0.55rem;
	border-radius: 50%;
	background: var(--ap-muted);
	&--ok { background: var(--ap-ok); }
	&--warn { background: var(--ap-warn); }
	&--bad { background: var(--ap-alert); }
	&--progress { background: var(--ap-teal); }
	&--off { background: var(--ap-line-strong); }
}
.rfc-status-label {
	font-weight: 500;
}
.rfc-track {
	height: 4px;
	border-radius: 2px;
	background: var(--ap-mist);
	overflow: hidden;
}
.rfc-track-fill {
	width: 35%;
	height: 100%;
	border-radius: 2px;
	background: var(--ap-teal);
	animation: rfc-slide 1.2s ease-in-out infinite;
}
@keyframes rfc-slide {
	from { transform: translateX(-100%); }
	to { transform: translateX(300%); }
}
@media (prefers-reduced-motion: reduce) {
	.rfc-track-fill {
		animation: none;
		width: 100%;
		opacity: 0.5;
	}
}
.rfc-error {
	margin: 0;
	font-size: 0.8rem;
	color: var(--ap-alert);
}
.rfc-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}
</style>
